import { useRef, useState, useCallback, useLayoutEffect, useMemo } from 'react';

export interface DropdownPosition {
  direction: 'above' | 'below';
  maxHeight: number;
  top: number;
  bottom: number;
  left: number;
  width: number;
  ready: boolean;
}
export function useDropdownPosition(
  isOpen: boolean,
  gap = 4,
  minHeight = 110,
) {
  const triggerRef = useRef<HTMLDivElement>(null);

  const [position, setPosition] = useState<DropdownPosition>({
    direction: 'below',
    maxHeight: 240,
    top: 0,
    bottom: 0,
    left: 0,
    width: 0,
    ready: false,
  });

  const calculate = useCallback(() => {
    if (!triggerRef.current) return;

    const rect = triggerRef.current.getBoundingClientRect();
    const vh = window.innerHeight;
    const vw = window.innerWidth;
    const EDGE = 6;

    const headerEl = document.querySelector('header');
    const effectiveTop = headerEl
      ? headerEl.getBoundingClientRect().bottom + EDGE
      : EDGE;
    const footerEl = document.querySelector('footer');
    const footerTop = footerEl
      ? footerEl.getBoundingClientRect().top
      : vh;
    const effectiveBottom = Math.min(vh, footerTop) - EDGE;

    const spaceBelow = effectiveBottom - rect.bottom - gap;
    const spaceAbove = rect.top - effectiveTop - gap;

    const MAX = 320;
    const clamp = (v: number) => Math.min(Math.max(v, 0), MAX);

    // The menu normally matches its trigger, but a narrow trigger produced a
    // menu so thin every option truncated to a letter ("C…", "H…"). Give it a
    // floor, then pull it back inside the viewport if that floor pushes it off
    // the right edge — a wider menu than its trigger is fine; an unreadable one
    // is not.
    const MIN_WIDTH = 200;
    const width = Math.min(Math.max(rect.width, MIN_WIDTH), vw - gap * 2);
    const left = Math.max(gap, Math.min(rect.left, vw - width - gap));

    const common = {
      top: rect.bottom + gap,
      bottom: vh - rect.top + gap,
      left,
      width,
      ready: true,
    };

    if (spaceBelow >= minHeight || spaceBelow >= spaceAbove) {
      setPosition({ direction: 'below', maxHeight: clamp(spaceBelow), ...common });
    } else {
      setPosition({ direction: 'above', maxHeight: clamp(spaceAbove), ...common });
    }
  }, [gap, minHeight]);

  useLayoutEffect(() => {
    if (!isOpen) {
      setPosition(p => ({ ...p, ready: false }));
      return;
    }

    calculate();

    const onUpdate = () => requestAnimationFrame(calculate);
    window.addEventListener('scroll', onUpdate, true);
    window.addEventListener('resize', onUpdate);

    return () => {
      window.removeEventListener('scroll', onUpdate, true);
      window.removeEventListener('resize', onUpdate);
    };
  }, [isOpen, calculate]);

  return useMemo(() => ({ triggerRef, position }), [position]);
}

export default useDropdownPosition;
